import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Table } from 'design-system-web';
import { AdminPage } from 'components';
import { H3 } from 'components/typography';

import type { InspectionsChannel } from '../types/channel';
import type { InspectionTemplate } from 'inspection-types';
import { useGetInspectionTemplates } from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';
import styles from './InspectionsTemplateLibrary.scss';
import {
  type TemplateLibraryTableRow,
  mapTemplateToTableRow,
  getTemplateLibraryColumns,
  getTemplateLibraryRowActions,
} from './templateLibraryTableConfig';
import { useCustomizeInspectionTemplate } from '../hooks/useCustomizeInspectionTemplate';
import { useInspectionTemplateHideEnabled } from '../hooks/useInspectionTemplateHideEnabled';
import {
  useToggleInspectionTemplateVisibility,
  type VisibilityChangedResult,
} from '../hooks/useToggleInspectionTemplateVisibility';

type Props = {
  channel: InspectionsChannel;
  /** Channel admin route prefix (e.g. match.path from portal), without trailing slash */
  channelBasePath: string;
};

export function InspectionsTemplateLibrary({
  channel,
  channelBasePath,
}: Props) {
  const { t } = useTranslation();
  const { templates, loading, error, fetchInspectionTemplates, called } =
    useGetInspectionTemplates();
  const { customizeTemplate } = useCustomizeInspectionTemplate({
    channel,
    channelBasePath,
  });
  const hideTemplateEnabled = useInspectionTemplateHideEnabled(channel?._id);

  const loadTemplates = useCallback((): Promise<void> => {
    if (!channel?._id) {
      return Promise.resolve();
    }

    const request = fetchInspectionTemplates({
      buildingId: channel._id,
      entityType: 'building',
      sourceType: SourceType.SourceTypeActivate,
      includeHidden: hideTemplateEnabled ? true : undefined,
    });

    // This screen relies on hook state for error UI; the promise resolves
    // either way so callers (the toggle-visibility flow) can await the fetch
    // settling without needing to handle its rejection themselves.
    return request
      ? request.then(() => undefined).catch(() => undefined)
      : Promise.resolve();
  }, [channel?._id, fetchInspectionTemplates, hideTemplateEnabled]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const [visibilityOverrides, setVisibilityOverrides] = useState<
    Record<string, boolean>
  >({});

  // When a fork occurs during hide/unhide, the refetched list may still
  // contain the parent template instead of the fork (projection lag). This
  // state maps forkedTemplateId → a synthetic template that replaces the
  // parent row so the user sees the change immediately.
  const [templateReplacements, setTemplateReplacements] = useState<
    Record<string, { parentId: string; syntheticTemplate: InspectionTemplate }>
  >({});

  const handleVisibilityChanged = useCallback(
    async ({ templateId, hidden, forkedFromTemplateId }: VisibilityChangedResult) => {
      if (forkedFromTemplateId) {
        // A fork occurred — build a synthetic template from the parent so the
        // row reflects the change immediately, even if the refetch is stale.
        setTemplateReplacements(prev => {
          const parentTemplate = templates.find(
            tpl => tpl.id === forkedFromTemplateId
          );

          if (!parentTemplate) {
            return prev;
          }

          const syntheticTemplate: InspectionTemplate = {
            ...parentTemplate,
            id: templateId,
            hidden,
            parentTemplateId: forkedFromTemplateId,
            metadata: {
              ...parentTemplate.metadata,
              buildingId: parentTemplate.metadata.buildingId
                ? {
                    ...parentTemplate.metadata.buildingId,
                    entityId: channel._id,
                  }
                : undefined,
            },
          };

          return {
            ...prev,
            [templateId]: { parentId: forkedFromTemplateId, syntheticTemplate },
          };
        });
      } else {
        // Owned template — use the lightweight visibility override as before.
        setVisibilityOverrides(prev => ({ ...prev, [templateId]: hidden }));
      }

      await loadTemplates();
    },
    [channel._id, loadTemplates, templates]
  );

  const rawTemplatesById = useMemo(
    () => new Map(templates.map(tpl => [tpl.id, tpl])),
    [templates]
  );

  useEffect(() => {
    setVisibilityOverrides(prev => {
      const settledIds = Object.keys(prev).filter(id => {
        const template = rawTemplatesById.get(id);

        return template !== undefined && template.hidden === prev[id];
      });

      if (settledIds.length === 0) {
        return prev;
      }

      const next = { ...prev };

      settledIds.forEach(id => delete next[id]);

      return next;
    });

    // Clean up template replacements once the real fork appears in the
    // fetched list, or the parent is gone (server replaced it).
    setTemplateReplacements(prev => {
      const settledForkIds = Object.keys(prev).filter(forkedId => {
        const realFork = rawTemplatesById.get(forkedId);

        // The fork has landed in the read model — drop the synthetic row.
        if (realFork !== undefined) {
          return true;
        }

        // The parent template is also gone — the projection removed it after
        // the fork, so the synthetic row is all that's left. Keep it.
        return false;
      });

      if (settledForkIds.length === 0) {
        return prev;
      }

      const next = { ...prev };

      settledForkIds.forEach(id => delete next[id]);

      return next;
    });
  }, [rawTemplatesById]);

  // Set of parent template ids that have been replaced by a fork. Used to
  // filter them out of the rendered list so the stale parent row disappears.
  const replacedParentIds = useMemo(
    () => new Set(Object.values(templateReplacements).map(r => r.parentId)),
    [templateReplacements]
  );

  const effectiveTemplates = useMemo(
    () => {
      // Start with the fetched templates, filtering out any parents that were
      // replaced by a fork, then apply owned-template visibility overrides.
      const base = templates
        .filter(template => !replacedParentIds.has(template.id))
        .map(template => {
          const override = visibilityOverrides[template.id];

          return override === undefined
            ? template
            : { ...template, hidden: override };
        });

      // Append synthetic fork templates. If the real fork has landed in the
      // fetched list it will already be in `base` (the settle effect above
      // cleans up the replacement entry), so this only adds rows while the
      // projection is still stale.
      const syntheticTemplates = Object.values(templateReplacements).map(
        r => r.syntheticTemplate
      );

      return [...base, ...syntheticTemplates];
    },
    [templates, visibilityOverrides, replacedParentIds, templateReplacements]
  );

  const templatesById = useMemo(
    () => new Map(effectiveTemplates.map(tpl => [tpl.id, tpl])),
    [effectiveTemplates]
  );

  const { toggleTemplateVisibility } = useToggleInspectionTemplateVisibility({
    channel,
    onVisibilityChanged: handleVisibilityChanged,
  });

  const rows = useMemo(
    () => effectiveTemplates.map(mapTemplateToTableRow),
    [effectiveTemplates]
  );

  const filterTemplatesByName = useCallback(
    (row: TemplateLibraryTableRow, keyword: string) => {
      const q = keyword.trim().toLowerCase();

      if (!q) {
        return true;
      }

      return row.name.toLowerCase().includes(q);
    },
    []
  );

  const columns = useMemo(
    () => getTemplateLibraryColumns(t, { includeVisibilityColumn: hideTemplateEnabled }),
    [t, hideTemplateEnabled]
  );

  // Isolated per column set — see the comment on the Table's `key`/`tableKey`
  // props below for why the visibility column needs its own persisted-order
  // cache bucket rather than sharing one with the base (no-visibility) table.
  const tableKey = `inspections-template-library-v2${
    hideTemplateEnabled ? '-visibility' : ''
  }`;

  const handleCustomize = useCallback(
    (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      customizeTemplate(template);
    },
    [templatesById, customizeTemplate]
  );

  const handleToggleVisibility = useCallback(
    (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      toggleTemplateVisibility(template);
    },
    [templatesById, toggleTemplateVisibility]
  );

  const isChildChannel = Boolean(channel.parent?._id);

  const rowActions = useMemo(
    () =>
      isChildChannel
        ? getTemplateLibraryRowActions(t, {
            onCustomize: handleCustomize,
            onToggleVisibility: handleToggleVisibility,
            includeVisibilityActions: hideTemplateEnabled,
          })
        : undefined,
    [isChildChannel, t, handleCustomize, handleToggleVisibility, hideTemplateEnabled]
  );

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.templateLibrary}>
          <div className={styles.header}>
            <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
          </div>
          <p>{t('web.admin.inspections.templateLibrary.error')}</p>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.templateLibrary}>
        <div className={styles.header}>
          <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
        </div>
        <Table<TemplateLibraryTableRow>
          columns={columns}
          data={rows}
          isLoading={loading || !called}
          pagination="client"
          defaultPageSize={25}
          rowActions={rowActions}
          rowActionsLabel={t(
            'web.admin.inspections.templateLibrary.actions.label'
          )}
          emptyMessage={t('web.admin.inspections.templateLibrary.emptyState')}
          hasKeywordFilter
          keywordFilterLabel={t(
            'web.admin.inspections.templateLibrary.searchLabel'
          )}
          customKeywordFilterFn={filterTemplatesByName}
          getRowId={row => row.id}
          showColumnVisibility
          // `getMergedColumnOrder` (design-system-web) drops a column key from the
          // persisted order once it disappears from `columns`, then re-appends it
          // at the END the next time it reappears — so if the visibility column
          // shared a cache key with the base table, toggling the hide-template
          // flag off then on would push it to the end every time. Keying the two
          // column sets separately keeps each one's persisted order isolated, so
          // the flag can flip without corrupting the other's column order.
          //
          // `key` forces a remount (not just a prop change) when the flag flips,
          // since usePersistedSetting/useLocalStorage only reads localStorage in
          // its useState initializer — that won't re-run just because the
          // `tableKey` prop changed on an already-mounted Table.
          key={tableKey}
          tableKey={tableKey}
        />
      </div>
    </AdminPage>
  );
}
